import { z, tenantSlugSchema, uuidSchema, ipOrCidrSchema } from '@kc3/validation';
/**
 * Live call assist engine picked at provisioning — 'aws' (Transcribe
 * streaming + cloud LLM routing) or 'self_hosted' (faster-whisper service +
 * a pinned local LLM such as ollama:gemma4:e4b). Seeds the tenant's
 * live_assist_settings; the tenant admin can change it afterwards.
 */
export const LiveAssistEngineFields = {
    liveAssistEngine: z.enum(['aws', 'self_hosted']).optional(),
    liveAssistWhisperUrl: z
        .string()
        .trim()
        .max(500)
        .refine((v) => v === '' || /^(https?|wss?):\/\/[^\s/]+(\/\S*)?$/.test(v), { message: 'Invalid Whisper server URL' })
        .nullable()
        .optional(),
    /** Write-only. Blank/omitted keeps the stored key, null clears it. */
    liveAssistWhisperKey: z.string().max(256).nullable().optional(),
    liveAssistModel: z
        .string()
        .trim()
        .max(200)
        .refine((v) => v === '' || /^[a-z][a-z0-9_-]*:[A-Za-z0-9._:/-]+$/.test(v), { message: 'Use provider:model, e.g. ollama:gemma4:e4b' })
        .nullable()
        .optional(),
};
export const CreateTenantRequestSchema = z.object({
    slug: tenantSlugSchema,
    name: z.string().min(2).max(200),
    planId: uuidSchema.optional(),
    status: z.enum(['active', 'suspended']).optional(),
    // Basic Info configuration
    domainUrl: z.string().optional().transform(val => val ? val.trim() : val).refine(val => !val || val === '' || /^https?:\/\/.+/.test(val), { message: 'Invalid URL format' }),
    timeZone: z.string().optional(),
    userLimitation: z.string().optional(),
    // Client Details
    clientFullName: z.string().optional(),
    clientEmail: z.string().optional(),
    clientMobileNumber: z.string().regex(/^\d{0,15}$/, 'Mobile number must contain only digits and be at most 15 characters').optional(),
    clientLandline: z.string().regex(/^\d{0,15}$/, 'Landline number must contain only digits and be at most 15 characters').optional(),
    clientAddress: z.string().optional(),
    // Company Details
    companyName: z.string().optional(),
    companyEmail: z.string().optional(),
    // AWS S3
    enableS3: z.boolean().optional(),
    awss3BucketName: z.string().optional(),
    awss3AccessKey: z.string().optional(),
    awss3Secret: z.string().optional(),
    awss3Region: z.string().optional(),
    awss3UrlExpiry: z.number().optional(),
    // Post-call analytics (AWS Transcribe). The secret is write-only.
    postCallAnalytics: z.boolean().optional(),
    awsTranscribeAccessKey: z.string().max(255).nullable().optional(),
    awsTranscribeSecret: z.string().max(255).optional(),
    awsTranscribeRegion: z.string().max(64).nullable().optional(),
    awsTranscribeS3Bucket: z.string().max(255).nullable().optional(),
    awsTranscribeLanguageCode: z.string().max(16).nullable().optional(),
    awsTranscribeIamArn: z.string().max(2048).nullable().optional(),
    // Agent-facing: upload external recordings into Call History.
    manualCallHistoryUpload: z.boolean().optional(),
    showAgentSentiment: z.boolean().optional(),
    ...LiveAssistEngineFields,
    // Feature flags (can be boolean or nested objects like googleSignIn)
    featureFlags: z.record(z.string(), z.any()).optional(),
    // Company type and default seed toggle
    companyType: z.string().optional().default('general'),
    seedDefaultData: z.boolean().optional().default(false),
});
export const TenantResponseSchema = z.object({
    id: uuidSchema,
    slug: tenantSlugSchema,
    name: z.string(),
    status: z.enum(['active', 'suspended', 'pending']),
    planId: uuidSchema.nullable(),
    schemaProvisioned: z.boolean(),
    createdAt: z.string().datetime(),
    // Basic Info configuration
    domainUrl: z.string().nullable(),
    timeZone: z.string().nullable(),
    userLimitation: z.string().nullable(),
    // Client Details
    clientFullName: z.string().nullable(),
    clientEmail: z.string().nullable(),
    clientMobileNumber: z.string().nullable(),
    clientLandline: z.string().nullable(),
    clientAddress: z.string().nullable(),
    // Company Details
    companyName: z.string().nullable(),
    companyEmail: z.string().nullable(),
    // AWS S3
    enableS3: z.boolean().nullable(),
    awss3BucketName: z.string().nullable(),
    awss3AccessKey: z.string().nullable(),
    awss3Secret: z.string().nullable(),
    awss3Region: z.string().nullable(),
    awss3UrlExpiry: z.number().nullable(),
    // Post-call analytics — the secret is never returned, only whether one is set.
    postCallAnalytics: z.boolean().optional(),
    awsTranscribeAccessKey: z.string().nullable().optional(),
    awsTranscribeSecretConfigured: z.boolean().optional(),
    awsTranscribeRegion: z.string().nullable().optional(),
    awsTranscribeS3Bucket: z.string().nullable().optional(),
    awsTranscribeLanguageCode: z.string().nullable().optional(),
    awsTranscribeIamArn: z.string().nullable().optional(),
    manualCallHistoryUpload: z.boolean().optional(),
    showAgentSentiment: z.boolean().optional(),
    liveAssistEngine: z.enum(['aws', 'self_hosted']).optional(),
    liveAssistWhisperUrl: z.string().nullable().optional(),
    liveAssistWhisperKeyConfigured: z.boolean().optional(),
    liveAssistModel: z.string().nullable().optional(),
    // Feature flags
    featureFlags: z.record(z.string(), z.unknown()).nullable(),
    // Company type and default seed toggle
    companyType: z.string().nullable(),
    seedDefaultData: z.boolean().nullable(),
});
// User Portal IP Whitelist
export const CreateUserPortalIpWhitelistRequestSchema = z.object({
    status: z.enum(['active', 'inactive']).optional(),
    aclName: z.string().min(1).max(128),
    aclDescription: z.string().optional(),
    ips: z.array(ipOrCidrSchema).default([]),
});
export const UpdateUserPortalIpWhitelistRequestSchema = CreateUserPortalIpWhitelistRequestSchema.partial();
export const UserPortalIpWhitelistResponseSchema = z.object({
    id: uuidSchema,
    status: z.enum(['active', 'inactive']),
    aclName: z.string(),
    aclDescription: z.string().nullable(),
    ips: z.array(z.string()),
    createdBy: uuidSchema.nullable(),
    updatedBy: uuidSchema.nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
//# sourceMappingURL=tenants.js.map